import { Inject, Injectable } from '@angular/core';
import { AppSettingsService, CustomWindow, WINDOW } from '@woolworthsnz/styleguide';
import { ProductSearchSettings } from '@woolworthsnz/trader-api';
import { SearchMediatorService } from '../search-mediator.service';

@Injectable({
	providedIn: 'root',
})
export class InfiniteScrollingService {
	private maximumNumberOfAutoScrolls: number;
	private numberOfWindowsThreshold: number;
	private numberOfAutoScrolls: number;

	constructor(
		@Inject(WINDOW) private window: CustomWindow,
		private searchMediatorService: SearchMediatorService,
		appSettings: AppSettingsService
	) {
		const settings: ProductSearchSettings | undefined = appSettings.getSetting('productSearchSettings');
		this.maximumNumberOfAutoScrolls = settings?.maximumPagesToAutoLoad || 2;
		this.numberOfWindowsThreshold = settings?.thresholdToLoadMoreOnScroll || 1;
	}

	/**
	 * Sets up infinite scroller that fires "Load more" events to the searchMediatorService
	 * when reaching the bottom of the page
	 *
	 * @param anchorElement anchor element to observe against, this will determine where the bottom of the page is
	 * @param shouldLoadMoreFn function that returns a boolean to determine whether the next page should load
	 */
	public setupInfiniteScroller(
		anchorElement: HTMLElement,
		shouldLoadMoreFn: () => boolean
	): IntersectionObserver | undefined {
		if (!('IntersectionObserver' in this.window)) {
			return undefined;
		}

		// automatically loads next page dynamically when reaching the bottom of the page
		// will only do it upto the maximumNumberOfAutoScrolls defined
		const scrollFromBottom = this.window.innerHeight * this.numberOfWindowsThreshold;
		this.resetNumberOfAutoScrollsCount();
		const intersectionObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (
						entry.isIntersecting &&
						entry.boundingClientRect.top > scrollFromBottom &&
						this.numberOfAutoScrolls < this.maximumNumberOfAutoScrolls &&
						shouldLoadMoreFn()
					) {
						this.searchMediatorService.emitLoadMoreEvent();
						this.numberOfAutoScrolls++;
					}
				});
			},
			// the anchor element has to be a screen away for the reload to trigger
			{ rootMargin: `${scrollFromBottom}px` }
		);

		intersectionObserver.observe(anchorElement);

		return intersectionObserver;
	}

	/**
	 * Resets the auto scroll count
	 */
	public resetNumberOfAutoScrollsCount(): void {
		this.numberOfAutoScrolls = 0;
	}
}
